import {
  Component,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ImageComponent } from '../../atoms/image/image.component';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { PodcastService } from '../../../services/podcast.service';
import { PodcastPlatforms } from '../../../shared/interfaces/podcast-item.interface';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-logo-wall',
  standalone: true,
  imports: [CommonModule, ImageComponent, NgxSkeletonLoaderModule],
  templateUrl: './logo-wall.component.html',
  styleUrls: ['./logo-wall.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class LogoWallComponent implements OnChanges {
  @Input() podcastPlatforms: string[] = [];
  @Input() caasPodcastPlatforms?: PodcastPlatforms;
  @Input() caasData?: CaasComponent;
  @Input() headline?: string;

  googleLogo: { logoSrc?: string; alt?: string; label?: string; url?: string } =
    {};
  appleLogo: { logoSrc?: string; alt?: string; label?: string; url?: string } =
    {};
  deezerLogo: { logoSrc?: string; alt?: string; label?: string; url?: string } =
    {};
  spotifyLogo: {
    logoSrc?: string;
    alt?: string;
    label?: string;
    url?: string;
  } = {};
  overcastLogo: {
    logoSrc?: string;
    alt?: string;
    label?: string;
    url?: string;
  } = {};
  public isLoading = true;

  constructor(
    private readonly podcastService: PodcastService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  initializeLogoWallItems(platform: PodcastPlatforms['platforms'][number]) {
    let alt: string | undefined;
    let label: string | undefined;
    let url: string | undefined;
    safeAssign(
      () => {
        alt = platform.alt;
      },
      'Assigning Logo Src',
      this.errorHandler
    );
    safeAssign(
      () => {
        label = platform.label;
      },
      'Assigning Logo Label',
      this.errorHandler
    );
    safeAssign(
      () => {
        url = platform.url;
      },
      'Assigning Logo URL',
      this.errorHandler
    );
    switch (label) {
      case 'GooglePodcasts':
        safeAssign(
          () => {
            this.googleLogo = {
              logoSrc: './assets/images/google_logo.webp',
              alt: alt,
              label: label,
              url: url,
            };
          },
          'Assigning Google Logo',
          this.errorHandler
        );
        break;
      case 'ApplePodcasts':
        safeAssign(
          () => {
            this.appleLogo = {
              logoSrc: './assets/images/apple_logo.webp',
              alt: alt,
              label: label,
              url: url,
            };
          },
          'Assigning Apple Logo',
          this.errorHandler
        );
        break;
      case 'deezer':
        safeAssign(
          () => {
            this.deezerLogo = {
              logoSrc: './assets/images/deezer_logo.webp',
              alt: alt,
              label: label,
              url: url,
            };
          },
          'Assigning Deezer Logo',
          this.errorHandler
        );
        break;
      case 'Spotify':
        safeAssign(
          () => {
            this.spotifyLogo = {
              logoSrc: './assets/images/spotify_logo.webp',
              alt: alt,
              label: label,
              url: url,
            };
          },
          'Assigning Spotify Logo',
          this.errorHandler
        );
        break;
      case 'Overcast':
        safeAssign(
          () => {
            this.overcastLogo = {
              logoSrc: './assets/images/overcast_logo.webp',
              alt: alt,
              label: label,
              url: url,
            };
          },
          'Assigning Overcast Logo',
          this.errorHandler
        );
        break;
      default:
        break;
    }
  }

  setCaasContent() {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_header?.value) {
          this.headline = this.caasData.formData.st_header.value;
          let platforms: undefined | PodcastPlatforms = undefined;
          safeAssign(
            () => {
              platforms = this.podcastService.getPodcastPlatformBySerieId(
                this.caasData?.formData?.st_podcast?.value?.identifier
              );
            },
            'Assigning Podcast Platforms',
            this.errorHandler
          );
          if (platforms) {
            this.caasPodcastPlatforms = platforms;
            safeAssign(
              () => {
                this.caasPodcastPlatforms?.platforms.forEach((platform) => {
                  this.initializeLogoWallItems(platform);
                });
              },
              'Initializing Logo Wall Items',
              this.errorHandler
            );
          }
        }
      },
      'Assigning Headline and Caas Content',
      this.errorHandler
    );
  }
}
